import { useRef, useState } from "react";
import { axiosPost } from "@root/shared/utils";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";

export type AutoSelectStatus = "idle" | "loading" | "completed" | "error";

// ユーザーIDをキー、割当求人IDを値とするマップ（マッチする求人が無いユーザーは空文字）
type AutoSelectedWorkIds = Record<string, string>;

type AutoSelectResult = {
  status: "success" | "failure";
};

type AutoSelectWorksResponse = {
  status: number;
  // ユーザーIDをキー、割当求人IDを値とするマップ（マッチする求人が無いユーザーは null）
  work_ids?: Record<string, number | null>;
};

const ErrorMessage = "通信に失敗しました。時間をおいて再度お試しください。";

export const useAutoSelectWork = (autoSelectWorkPath: string, selectedUserIds: number[]) => {
  const [autoSelectStatus, setAutoSelectStatus] = useState<AutoSelectStatus>("idle");
  const [autoSelectedWorkIds, setAutoSelectedWorkIds] = useState<AutoSelectedWorkIds>({});
  // 実行中に選択ユーザーが変わった応答を適用しないよう、応答適用時の整合確認用に最新の選択を保持する
  const selectedUserIdsRef = useRef(selectedUserIds);
  selectedUserIdsRef.current = selectedUserIds;

  const executeAutoSelect = async (): Promise<AutoSelectResult> => {
    setAutoSelectStatus("loading");
    setAutoSelectedWorkIds({});

    try {
      const { data } = await axiosPost.post<AutoSelectWorksResponse>(autoSelectWorkPath, {
        selected_user_ids: selectedUserIds,
      });

      // work_ids を含まない status 200 応答（デプロイ跨ぎで旧サーバーが応答した場合等）は通信エラーと同様に扱う
      if (data.status === 200 && data.work_ids) {
        const workIds: AutoSelectedWorkIds = {};
        Object.entries(data.work_ids).forEach(([userId, workId]) => {
          workIds[userId] = workId == null ? "" : String(workId);
        });
        // 現在の選択ユーザー全員分の割当が揃っていない応答は適用せず破棄する（おまかせ選択が解除される）
        const isStale = selectedUserIdsRef.current.some((userId) => !(String(userId) in workIds));
        if (isStale) {
          setAutoSelectStatus("idle");
          return { status: "failure" };
        }
        setAutoSelectedWorkIds(workIds);
        setAutoSelectStatus("completed");
        return { status: "success" };
      }

      setAutoSelectStatus("error");
      alert(ErrorMessage);
      return { status: "failure" };
    } catch (error) {
      sendErrorLog(error as Error);

      setAutoSelectStatus("error");
      alert(ErrorMessage);
      return { status: "failure" };
    }
  };

  const resetAutoSelectState = () => {
    setAutoSelectStatus("idle");
    setAutoSelectedWorkIds({});
  };

  return {
    autoSelectStatus,
    autoSelectedWorkIds,
    executeAutoSelect,
    resetAutoSelectState,
  };
};
